import * as React from 'react'
import { action, observable } from 'mobx'
import { task, TaskOptions, Task, TaskGroup } from 'mobx-task'
import values from 'lodash/values'
import { useObservableStream } from './rx-hooks'
import { toStream } from 'utils/mobx-util'
import { first, map } from 'rxjs/operators'

/**
 * Hook for MobX-Task.
 */
export function useTask<A extends any[], R>(
  func: (...args: A) => R,
  options?: TaskOptions<A, R>,
  deps?: any[],
): Task<A, R> {
  const optsMemo = React.useMemo(() => options, values(options))
  return React.useMemo(() => task(func, options), [optsMemo, ...(deps || [])])
}

useTask.resolved = <A extends any[], R>(
  func: (...args: A) => R,
  deps?: any[],
): Task<A, R> =>
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useTask(func, { state: 'resolved' }, deps)
useTask.pending = <A extends any[], R>(
  func: (...args: A) => R,
  deps?: any[],
): Task<A, R> =>
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useTask(func, { state: 'pending' }, deps)

export function useTaskGroup<A extends any[], R>(
  tasks: Array<Task<A, R>>,
): TaskGroup<A, R> {
  return React.useMemo(() => TaskGroup(tasks), tasks)
}

/**
 * Returns a `bool` for whether the task is currently running for the first time.
 *
 * @param task
 * @param defaultValue
 */
export function useIsTaskCurrentlyRunningForTheFirstTime(
  task: Task<any, any>,
  defaultValue = true,
) {
  // This works by subscribing to the first time `resolved` turns `true`.
  // Once it does, we emit `false` (because we are no longer waiting any more).
  return useObservableStream(
    defaultValue,
    () =>
      toStream(() => task.resolved, true).pipe(
        first(Boolean),
        map(() => false),
      ),
    [task],
  )
}

export interface Activatable {
  activate(): unknown

  deactivate?(): unknown
}

/**
 * Basic Activation life cycle.
 *
 * @param activatable
 * @param deps
 */
export function useActivation(
  activatable: Activatable,
  deps: readonly any[] = [],
) {
  React.useEffect(() => {
    activatable.activate()
    return () => {
      activatable.deactivate?.()
    }
  }, [
    activatable,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...deps,
  ])
}

/**
 * Like `useRef` but `observer`s will react to it.
 */
export function useObservableRef<T>(initialValue: T) {
  const [box] = React.useState(() => {
    const store = observable(
      {
        current: initialValue,
        set: (newValue: T) => {
          store.current = newValue
        },
      },
      { current: observable.ref, set: action },
    )
    return store
  })
  return box
}
